import type { FilterGroup, FilterState, StoredFilterCondition, StoredFilterGroup } from '@/types/deviceFilters'

const compactCondition = (condition: FilterState): StoredFilterCondition => {
	const { id: _id, fieldLabel: _fieldLabel, operatorLabel, valueLabels, ...rest } = condition
	const values = Array.isArray(condition.value) ? condition.value.map(String) : []
	const labelsDiffer =
		valueLabels !== undefined &&
		(valueLabels.length !== values.length || valueLabels.some((label, index) => label !== values[index]))
	return {
		...rest,
		...(operatorLabel !== undefined && operatorLabel !== condition.operator ? { operatorLabel } : {}),
		...(labelsDiffer ? { valueLabels } : {}),
	}
}

/**
 * What tells one set of groups from another: every part the built query reads, and nothing else.
 *
 * Ids stay out, since they are regenerated on every round trip through a link or a config. A multi
 * value condition is a set, so its values sort: re ticking one appends it without changing the filter.
 * A group with no conditions contributes nothing to the query and so nothing here either.
 */
export const filterGroupsSignature = (groups: FilterGroup[]): string =>
	JSON.stringify(
		groups
			.filter(group => group.conditions.length > 0)
			.map(group => [
				group.operator,
				group.joiner,
				group.conditions.map(condition => [
					condition.field,
					condition.type,
					condition.operator,
					Array.isArray(condition.value) ? [...condition.value].sort() : condition.value,
					condition.isNullOperator,
				]),
			]),
	)

/** `locked` goes too: it is the reader's to decide, not the stored group's. */
export const compactGroups = (groups: FilterGroup[]): StoredFilterGroup[] =>
	groups.map(({ id: _id, locked: _locked, ...group }) => ({
		...group,
		conditions: group.conditions.map(compactCondition),
	}))

/** Ids derive from `idPrefix`, so the same stored groups expand to the same objects every time. */
export const expandGroups = (groups: StoredFilterGroup[] | undefined, idPrefix: string): FilterGroup[] =>
	(Array.isArray(groups) ? groups : []).map((group, groupIndex) => ({
		...group,
		id: group.id ?? `${idPrefix}-g${groupIndex}`,
		conditions: (Array.isArray(group.conditions) ? group.conditions : []).map((condition, conditionIndex) => ({
			...condition,
			id: condition.id ?? `${idPrefix}-g${groupIndex}-c${conditionIndex}`,
		})),
	}))
